"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.apiKeyRolesForScope = exports.apiKeyRolesAcceptedForScope = exports.legacyApiKeyRoleForScope = exports.isLegacyApiKeyRole = exports.isAssignableAtCreate = exports.isApiKeyRole = exports.roleHasProjectPolicy = exports.systemRoleAccessRights = exports.isOrgAction = exports.allOrganizationActions = exports.allProjectActions = void 0;
const organizationAccessRights_1 = require("./organizationAccessRights");
const projectAccessRights_1 = require("./projectAccessRights");
/** allProjectActions is the full project action vocabulary. */
exports.allProjectActions = [...projectAccessRights_1.projectScopes];
/** allOrganizationActions is the full organization action vocabulary. */
exports.allOrganizationActions = [
    ...organizationAccessRights_1.organizationScopes,
];
/** organizationActionSet is allOrganizationActions indexed for membership tests. */
const organizationActionSet = new Set(organizationAccessRights_1.organizationScopes);
/** isOrgAction reports whether an action belongs to the disjoint org vocabulary. */
const isOrgAction = (action) => organizationActionSet.has(action);
exports.isOrgAction = isOrgAction;
/** allow builds a resource-less `SystemRolePolicy`, correlating its action vocabulary to the resource kind. */
const allow = (resourceKind, actions, effect = "ALLOW") => ({ resourceKind, actions, effect });
/** userRoleAccessRights builds a user role's org- and project-kind policies from the per-role access-right tables. */
const userRoleAccessRights = (role) => [
    allow("organization", organizationAccessRights_1.organizationRoleAccessRights[role]),
    allow("project", projectAccessRights_1.projectRoleAccessRights[role]),
];
/** orgKeyProjectActions are the project-kind actions an ORGANIZATION key holds against its own projects. */
const orgKeyProjectActions = [
    "project:read",
    "apiKeys:read",
    "apiKeys:CUD",
    "projectMembers:read",
    "projectMembers:CUD",
    "projectMembers:manageOwnership",
    "project:update",
    "project:delete",
];
/** projectKeyActions is the project vocabulary a PROJECT key holds, less the project-administration actions reserved for ORGANIZATION keys and the session user. */
const projectKeyActions = exports.allProjectActions.filter((action) => action === "project:read" || !orgKeyProjectActions.includes(action));
/** systemRoleAccessRights maps each `SystemRole` to its rich definition; the resolver binds each policy to concrete org/project resources. */
exports.systemRoleAccessRights = {
    OWNER: {
        id: "OWNER",
        name: "Owner",
        description: "Full access, plus billing and account deletion.",
        policies: userRoleAccessRights("OWNER"),
        tags: ["principal:user"],
    },
    ADMIN: {
        id: "ADMIN",
        name: "Admin",
        description: "All read and write privileges.",
        policies: userRoleAccessRights("ADMIN"),
        tags: ["principal:user", "principal:apiKey"],
    },
    MEMBER: {
        id: "MEMBER",
        name: "Member",
        description: "Work within the organization and its projects.",
        policies: userRoleAccessRights("MEMBER"),
        tags: ["principal:user"],
    },
    VIEWER: {
        id: "VIEWER",
        name: "Viewer",
        description: "Read-only privileges.",
        policies: userRoleAccessRights("VIEWER"),
        tags: ["principal:user", "principal:apiKey"],
    },
    NONE: {
        id: "NONE",
        name: "None",
        description: "No organization or project access.",
        policies: userRoleAccessRights("NONE"),
        tags: ["principal:user"],
    },
    LEGACY_PROJECT_API_KEY: {
        id: "LEGACY_PROJECT_API_KEY",
        name: "Project API key",
        description: "Read and write within a single project.",
        policies: [allow("project", projectKeyActions)],
        tags: ["principal:apiKey", "legacy"],
    },
    LEGACY_ORGANIZATION_API_KEY: {
        id: "LEGACY_ORGANIZATION_API_KEY",
        name: "Organization API key",
        description: "Administer the organization and all of its projects.",
        policies: [
            allow("organization", exports.allOrganizationActions),
            allow("project", orgKeyProjectActions),
        ],
        tags: ["principal:apiKey", "legacy"],
    },
    INGEST: {
        id: "INGEST",
        name: "Ingestion",
        description: "Create scores and traces from application telemetry.",
        policies: [
            allow("project", [
                "traces:create",
                "scores:save",
                "media:create",
                "project:read",
            ]),
        ],
        tags: ["principal:apiKey"],
    },
    SCORES_INGEST: {
        id: "SCORES_INGEST",
        name: "Scores ingestion",
        description: "Submit scores from a web application.",
        policies: [allow("project", ["scores:save"])],
        tags: ["principal:apiKey"],
    },
    AI_GATEWAY: {
        id: "AI_GATEWAY",
        name: "AI Gateway",
        description: "Route model requests through the AI Gateway.",
        policies: [allow("organization", ["gateway:invoke"])],
        tags: ["principal:apiKey"],
    },
};
/** roleHasProjectPolicy reports whether a role grants any project-kind policy. */
const roleHasProjectPolicy = (role) => exports.systemRoleAccessRights[role].policies.some((policy) => policy.resourceKind === "project");
exports.roleHasProjectPolicy = roleHasProjectPolicy;
/** isApiKeyRole reports whether a role may back an api key at all, including legacy roles still valid on existing keys. */
const isApiKeyRole = (role) => exports.systemRoleAccessRights[role].tags.includes("principal:apiKey");
exports.isApiKeyRole = isApiKeyRole;
/** isAssignableAtCreate reports whether a role is offered when creating a new api key: an api-key role that is not retired as legacy. */
const isAssignableAtCreate = (role) => (0, exports.isApiKeyRole)(role) && !exports.systemRoleAccessRights[role].tags.includes("legacy");
exports.isAssignableAtCreate = isAssignableAtCreate;
/** isLegacyApiKeyRole reports whether a role is a retired legacy api-key role. */
const isLegacyApiKeyRole = (role) => (0, exports.isApiKeyRole)(role) && exports.systemRoleAccessRights[role].tags.includes("legacy");
exports.isLegacyApiKeyRole = isLegacyApiKeyRole;
/** legacyApiKeyRoleForScope is the full-access role a scope's keys carried before per-key roles. */
const legacyApiKeyRoleForScope = (scope) => scope === "project"
    ? "LEGACY_PROJECT_API_KEY"
    : "LEGACY_ORGANIZATION_API_KEY";
exports.legacyApiKeyRoleForScope = legacyApiKeyRoleForScope;
/** apiKeyRolesAcceptedForScope is the roles a scope's create input accepts: the UI roles plus the scope's legacy role. */
const apiKeyRolesAcceptedForScope = (scope) => [
    ...(0, exports.apiKeyRolesForScope)(scope),
    (0, exports.legacyApiKeyRoleForScope)(scope),
];
exports.apiKeyRolesAcceptedForScope = apiKeyRolesAcceptedForScope;
/** apiKeyRolesForScope lists, in catalog order, the roles offered when creating an api key at the given scope: project-capable roles for a project key, every creatable api-key role for an organization key. */
const apiKeyRolesForScope = (scope) => Object.keys(exports.systemRoleAccessRights).filter((role) => scope === "project"
    ? (0, exports.isAssignableAtCreate)(role) && (0, exports.roleHasProjectPolicy)(role)
    : (0, exports.isAssignableAtCreate)(role));
exports.apiKeyRolesForScope = apiKeyRolesForScope;
